import { from, switchMap, type Observable } from 'rxjs';
import type { FilesApiService } from '../../../core/api/files-api.service';

/** Same constraints as the profile avatar upload, for cross-app consistency. */
export const PRODUCT_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const MAX_PRODUCT_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB
export const PRODUCT_IMAGE_MAX = 8;

/** Returns a PT-BR error message when the file is invalid, or null when it's OK. */
export function validateProductImage(file: File): string | null {
  if (!PRODUCT_IMAGE_CONTENT_TYPES.includes(file.type)) {
    return 'Formato inválido. Envie JPEG, PNG, WEBP ou GIF.';
  }
  if (file.size > MAX_PRODUCT_IMAGE_BYTES) {
    return 'Imagem muito grande. O limite é 2 MB.';
  }
  return null;
}

/**
 * Canonical upload flow (mirrors profile-settings avatar): issue presigned URL →
 * PUT the bytes straight to storage → confirm. Resolves to the confirmed fileId,
 * ready to attach to a product via the revenue API.
 */
export function uploadProductImage(files: FilesApiService, file: File): Observable<string> {
  return files
    .issueUploadUrl({ module: 'revenue', contentType: file.type, filename: file.name, sizeBytes: file.size })
    .pipe(
      switchMap((issued) =>
        from(
          fetch(issued.uploadUrl, {
            method: 'PUT',
            headers: issued.requiredHeaders,
            body: file,
          }).then((res) => {
            if (!res.ok) throw new Error(`upload falhou (${res.status})`);
            return issued.fileId;
          }),
        ),
      ),
      switchMap((fileId) => files.confirmUpload(fileId).pipe(switchMap(() => from([fileId])))),
    );
}
